import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, DollarSign, Coins, Plus, CheckCircle, XCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pay-as-you-go USD billing. No subscriptions, no credits, no packages.
// Protocol: DEPLOY-MAP.md 28.06.2026
const OP_LABELS = {
    brand_monitor: 'Brand monitoring',
    geo_monitor: 'Brand monitoring',
    location_monitor: 'Brand monitoring',
    sub_monitor: 'Product / service monitoring',
    auto_monitor: 'Brand monitoring',
    auto_brand: 'Brand monitoring',
    auto_product: 'Product / service monitoring',
    auto_location: 'Brand monitoring',
    chat_message: 'Chatbot message',
    chat_parse_url: 'URL parsing in chat',
    analyze_sources: 'Source analysis',
    generate_topics: 'Topic generation',
    generate_topics_url: 'Topics from URL',
    generate_article: 'Article writing',
    improve_article: 'Article improvement',
    generate_strategy: 'Strategy generation',
    get_insights: 'Strategic insights',
    custom_query: 'Custom queries',
    package_purchase: 'Package purchase',
    subscription_activate: 'Subscription',
    topup: 'Balance top-up',
    welcome_bonus: 'Welcome bonus',
    admin_manual: 'Admin adjustment',
    admin_refund: 'Admin refund',
    hypothesis_early_check: 'Hypothesis check',
    geo_audit: 'GEO audit',
};

const TX_TYPE_LABELS = {
    purchase: 'Top-up',
    debit: 'Charge',
    deduction: 'Charge',
    hold: 'Hold',
    bonus: 'Bonus',
    refund: 'Refund',
    auto_skipped: 'Skipped',
    admin_credit: 'Admin credit',
    admin_debit: 'Admin charge',
    expiry: 'Expired',
};

const BillingPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [txTotal, setTxTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [topupAmount, setTopupAmount] = useState('');
    const [topingUp, setTopingUp] = useState(false);

    // Payment return status
    const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'fail' | null
    const [pollingBalance, setPollingBalance] = useState(false);
    const pollTimerRef = useRef(null);
    const pollCountRef = useRef(0);

    useEffect(() => { loadData(); }, []);

    // Handle ?status=success/fail from Robokassa redirect
    useEffect(() => {
        const status = searchParams.get('status');
        if (status === 'success' || status === 'fail') {
            setPaymentStatus(status);
            // Clean URL without reload
            setSearchParams({}, { replace: true });

            if (status === 'success') {
                // Start polling: balance may not be credited yet (race condition)
                setPollingBalance(true);
                pollCountRef.current = 0;
                const poll = () => {
                    pollCountRef.current += 1;
                    loadData();
                    if (pollCountRef.current < 10) {
                        pollTimerRef.current = setTimeout(poll, 2000);
                    } else {
                        setPollingBalance(false);
                    }
                };
                pollTimerRef.current = setTimeout(poll, 1500);
            }
        }
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            const [walletRes, txRes] = await Promise.all([
                axios.get(`${API_URL}/api/billing/balance`, { withCredentials: true }),
                axios.get(`${API_URL}/api/billing/transactions?limit=20`, { withCredentials: true }),
            ]);
            setWallet(walletRes.data);
            setTransactions(txRes.data.transactions);
            setTxTotal(txRes.data.total);
        } catch (err) {
            console.error('Billing load error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Format cents to USD: 1200 -> "$12.00", 50 -> "$0.50"
    const fmtUSD = (cents) => {
        if (cents === null || cents === undefined) return '$0.00';
        return '$' + (cents / 100).toFixed(2);
    };

    const handleTopup = async () => {
        const amount = parseInt(topupAmount, 10);
        if (!amount || amount < 25 || amount > 1000) {
            toast.error('Amount must be between $25 and $1,000');
            return;
        }
        setTopingUp(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/billing/topup`,
                { amount },
                { withCredentials: true }
            );
            if (data.payment_url) {
                window.location.href = data.payment_url;
                return;
            }
            toast.success('Balance topped up successfully');
            setTopupAmount('');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Top-up error');
        } finally {
            setTopingUp(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-lg text-slate-500">Loading...</div>
            </div>
        );
    }

    const balance = wallet?.balance || 0;

    return (
        <div className="min-h-screen bg-white" data-testid="billing-page">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
                <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="text-slate-500 hover:text-slate-900 transition-colors"
                            data-testid="billing-back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <img src="/arpai_logo.png" alt="ARPAI" style={{ height: '1.25rem' }} />
                        <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
                        <span className="text-slate-300 mx-1">/</span>
                        <h1 className="text-base sm:text-lg tracking-tight font-bold text-slate-500">
                            Billing
                        </h1>
                    </div>
                    <div
                        className="flex items-center gap-3 bg-[#E8E5D6]/60 px-4 py-2"
                        data-testid="billing-balance-widget"
                    >
                        <DollarSign className="w-5 h-5 text-[#1BB594]" />
                        <span className="text-lg font-bold text-slate-900">{fmtUSD(balance)}</span>
                    </div>
                </div>
            </header>

            <main className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-12">
                {/* Payment status banner */}
                {paymentStatus === 'success' && (
                    <div className="bg-emerald-50 border border-emerald-200 p-5 mb-8 flex items-start gap-3" data-testid="payment-success-banner">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-emerald-800">Payment successful</p>
                            <p className="text-sm text-emerald-700 mt-1">
                                {pollingBalance
                                    ? 'Crediting your balance, please wait...'
                                    : 'Your balance has been updated.'}
                            </p>
                        </div>
                        {pollingBalance && <Loader2 className="w-4 h-4 text-emerald-500 animate-spin flex-shrink-0 mt-0.5" />}
                        <button onClick={() => setPaymentStatus(null)} className="text-emerald-400 hover:text-emerald-600">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {paymentStatus === 'fail' && (
                    <div className="bg-red-50 border border-red-200 p-5 mb-8 flex items-start gap-3" data-testid="payment-fail-banner">
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-red-800">Payment cancelled or failed</p>
                            <p className="text-sm text-red-700 mt-1">
                                No funds were charged. You can try again.
                            </p>
                        </div>
                        <button onClick={() => setPaymentStatus(null)} className="text-red-400 hover:text-red-600">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Balance card */}
                <div className="bg-white border border-[#E2DDD8] p-8 mb-10" data-testid="wallet-info">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                                Balance
                            </p>
                            <p className="text-3xl font-bold text-slate-900">{fmtUSD(balance)}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                                Total spent
                            </p>
                            <p className="text-xl font-semibold text-slate-700">
                                {fmtUSD(wallet?.total_spent || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                                Plan
                            </p>
                            <p className="text-xl font-semibold text-[#1BB594]">Pay-as-you-go</p>
                        </div>
                    </div>
                </div>

                {/* Low balance warning */}
                {balance > 0 && balance < 500 && (
                    <div
                        className="bg-amber-50 border border-amber-200 p-4 mb-8 flex items-center gap-3"
                        data-testid="low-balance-warning"
                    >
                        <Coins className="w-5 h-5 text-amber-500 flex-shrink-0" />
                        <p className="text-sm text-amber-800">
                            Your balance is running low. Top up to continue using the service.
                        </p>
                    </div>
                )}

                {/* Top-up section */}
                <div className="bg-white border border-[#E2DDD8] p-6 mb-10" data-testid="topup-section">
                    <h2 className="text-lg font-bold text-[#2F2E2F] mb-2">Top up balance</h2>
                    <p className="text-sm text-slate-500 mb-5">
                        Enter an amount between $25 and $1,000. Funds never expire.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            {[25, 50, 100, 250, 500, 1000].map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setTopupAmount(String(v))}
                                    className={`text-xs font-bold px-4 py-2 border transition-colors ${
                                        topupAmount === String(v)
                                            ? 'bg-[#1BB594] text-white border-[#1BB594]'
                                            : 'bg-white text-slate-700 border-[#E2DDD8] hover:bg-slate-50'
                                    }`}
                                    data-testid={`topup-preset-${v}`}
                                >
                                    ${v}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-slate-400 text-sm">$</span>
                            <input
                                type="number"
                                step="1"
                                min="25"
                                max="1000"
                                value={topupAmount}
                                onChange={(e) => setTopupAmount(e.target.value)}
                                placeholder="Amount"
                                className="w-28 px-3 py-2 border border-[#E2DDD8] text-sm focus:outline-none focus:border-[#1BB594]"
                                data-testid="topup-input"
                            />
                        </div>
                        <button
                            onClick={handleTopup}
                            disabled={topingUp || !topupAmount}
                            className="font-bold tracking-[0.15em] uppercase text-xs px-8 py-2.5 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50 flex items-center gap-2 transition-all active:translate-y-0.5"
                            data-testid="topup-submit-btn"
                        >
                            <Plus className="w-4 h-4" />
                            {topingUp ? 'Processing...' : 'Top up'}
                        </button>
                    </div>
                </div>

                {/* Transaction history */}
                <h2 className="text-xl font-semibold text-slate-900 mb-6">
                    Transaction history{' '}
                    <span className="text-sm text-slate-400 font-normal">({txTotal})</span>
                </h2>
                <div
                    className="bg-white border border-[#E2DDD8] overflow-hidden"
                    data-testid="transactions-table"
                >
                    {transactions.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No transactions yet</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#E2DDD8] bg-[#FAFAF8]">
                                    <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                                        Date
                                    </th>
                                    <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                                        Type
                                    </th>
                                    <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                                        Operation
                                    </th>
                                    <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                                        Amount
                                    </th>
                                    <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                                        Balance
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx, i) => {
                                    const isDebit =
                                        tx.type === 'debit' ||
                                        tx.type === 'deduction' ||
                                        tx.type === 'hold';
                                    return (
                                        <tr
                                            key={i}
                                            className="border-b border-[#E2DDD8] last:border-0 hover:bg-[#FAFAF8]"
                                        >
                                            <td className="px-6 py-3 text-slate-500">
                                                {new Date(
                                                    tx.created_at.endsWith('Z')
                                                        ? tx.created_at
                                                        : tx.created_at + 'Z'
                                                ).toLocaleString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false,
                                                })}
                                            </td>
                                            <td className="px-6 py-3">
                                                <span
                                                    className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${
                                                        isDebit
                                                            ? 'bg-red-50 text-red-600'
                                                            : tx.type === 'bonus'
                                                            ? 'bg-blue-50 text-blue-600'
                                                            : tx.type === 'purchase'
                                                            ? 'bg-emerald-50 text-emerald-600'
                                                            : tx.type === 'refund'
                                                            ? 'bg-cyan-50 text-cyan-600'
                                                            : 'bg-slate-50 text-slate-600'
                                                    }`}
                                                >
                                                    {TX_TYPE_LABELS[tx.type] || tx.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-slate-700">
                                                {OP_LABELS[tx.operation] || tx.operation || '—'}
                                            </td>
                                            <td
                                                className={`px-6 py-3 text-right font-mono font-semibold ${
                                                    isDebit ? 'text-red-600' : 'text-emerald-600'
                                                }`}
                                            >
                                                {isDebit ? '-' : '+'}
                                                {fmtUSD(tx.amount)}
                                            </td>
                                            <td className="px-6 py-3 text-right font-mono text-slate-600">
                                                {fmtUSD(tx.balance_after)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </main>
        </div>
    );
};

export default BillingPage;
